import React, { useState } from 'react';
import { Dialog } from '@blueprintjs/core';
import { ILeaseRecordingDetail } from 'src/models/dlcLease/ILeaseRecordingDetail';
import './RecordingDetailsDialog.css';

interface IProps {
    details: ILeaseRecordingDetail[];
    editable: boolean;
    onApply: (details: ILeaseRecordingDetail[]) => void;
    onClose: () => void;
}

interface IDetailDraft extends Omit<ILeaseRecordingDetail, 'noPages' | 'amount'> {
    noPages: string;
    amount: string;
}

const RecordingDetailsDialog: React.FC<IProps> = ({ details, editable, onApply, onClose }) => {
    const [rows, setRows] = useState<IDetailDraft[]>(() =>
        details.map((d) => ({
            ...d,
            noPages: d.noPages === null || d.noPages === undefined ? '' : String(d.noPages),
            amount: d.amount === null || d.amount === undefined ? '' : String(d.amount),
        }))
    );
    const [error, setError] = useState<string | null>(null);

    const change = (index: number, field: keyof IDetailDraft, value: string): void => {
        setRows((previous) =>
            previous.map((row, i) => (i === index ? { ...row, [field]: value, operation: row.operation === 'add' ? 'add' : 'update' } : row))
        );
        setError(null);
    };
    const remove = (index: number): void => {
        setRows((previous) =>
            previous[index].operation === 'add'
                ? previous.filter((_, i) => i !== index)
                : previous.map((row, i) => (i === index ? { ...row, operation: 'delete' } : row))
        );
    };
    const apply = (): void => {
        const active = rows.filter((row) => row.operation !== 'delete');
        if (
            active.some(
                (row) => row.noPages !== '' && (!/^-?\d+$/.test(row.noPages) || Number(row.noPages) < -2147483648 || Number(row.noPages) > 2147483647)
            )
        ) {
            setError('Pages must be a whole number between -2147483648 and 2147483647, or blank.');
            return;
        }
        if (active.some((row) => row.amount !== '' && (!/^-?\d+(\.\d{1,2})?$/.test(row.amount) || Math.abs(Number(row.amount)) > 9999999999.99))) {
            setError('Amount must have at most 10 whole digits and two decimal places, or be blank.');
            return;
        }
        onApply(
            rows.map((row) => ({
                ...row,
                noPages: row.noPages === '' ? null : Number(row.noPages),
                amount: row.amount === '' ? null : Number(row.amount),
            }))
        );
    };
    const total = rows.filter((row) => row.operation !== 'delete').reduce((sum, row) => sum + (Number(row.amount) || 0), 0);

    return (
        <Dialog
            isOpen={true}
            title="Recording Fee Details"
            onClose={onClose}
            className="recording-details-dialog"
            portalClassName="recording-details-portal"
            canOutsideClickClose={false}
        >
            <div className="recording-details-body">
                <p>Identify the documents covered by this recording. All fields and amounts are optional.</p>
                {error && <p role="alert">{error}</p>}
                <div className="recording-details-table-scroll">
                    <table className="recording-details-table">
                        <thead>
                            <tr>
                                <th>Lessor / Name on Document</th>
                                <th>Document Type</th>
                                <th>Req. No.</th>
                                <th>Legal Description</th>
                                <th>Pages</th>
                                <th>Amount</th>
                                {editable && <th>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) =>
                                row.operation === 'delete' ? null : (
                                    <tr key={index}>
                                        <td>
                                            <input
                                                aria-label={`Lessor / Name on Document ${index + 1}`}
                                                value={row.lessorName || ''}
                                                maxLength={400}
                                                readOnly={!editable}
                                                onChange={(e) => change(index, 'lessorName', e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                aria-label={`Document Type ${index + 1}`}
                                                value={row.documentType || ''}
                                                maxLength={30}
                                                readOnly={!editable}
                                                onChange={(e) => change(index, 'documentType', e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                aria-label={`Request Number ${index + 1}`}
                                                value={row.reqNo || ''}
                                                maxLength={30}
                                                readOnly={!editable}
                                                onChange={(e) => change(index, 'reqNo', e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <textarea
                                                aria-label={`Legal Description ${index + 1}`}
                                                value={row.shortLegal || ''}
                                                readOnly={!editable}
                                                onChange={(e) => change(index, 'shortLegal', e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                aria-label={`Pages ${index + 1}`}
                                                value={row.noPages}
                                                inputMode="numeric"
                                                readOnly={!editable}
                                                onChange={(e) => change(index, 'noPages', e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                aria-label={`Amount ${index + 1}`}
                                                value={row.amount}
                                                inputMode="decimal"
                                                readOnly={!editable}
                                                onChange={(e) => change(index, 'amount', e.target.value)}
                                            />
                                        </td>
                                        {editable && (
                                            <td>
                                                <button type="button" aria-label={`Remove detail ${index + 1}`} onClick={() => remove(index)}>
                                                    Remove
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                )
                            )}
                        </tbody>
                    </table>
                </div>
                {!rows.some((row) => row.operation !== 'delete') && <p>No document details have been added.</p>}
                <div className="recording-details-actions">
                    {editable && (
                        <button type="button" onClick={() => setRows([...rows, { id: 0, operation: 'add', noPages: '', amount: '' }])}>
                            Add Document
                        </button>
                    )}
                    <span>Detail total: {total.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
                </div>
                {editable && (
                    <p>
                        Apply keeps these edits in the recording editor. Save Recordings saves them. Detail amounts do not change the recording fee.
                    </p>
                )}
                <div className="recording-details-footer">
                    <button type="button" onClick={onClose}>
                        {editable ? 'Cancel' : 'Close'}
                    </button>
                    {editable && (
                        <button type="button" className="bp3-button bp3-intent-primary" onClick={apply}>
                            Apply Details
                        </button>
                    )}
                </div>
            </div>
        </Dialog>
    );
};

export default RecordingDetailsDialog;



// WEBPACK FOOTER //
// ./src/components/leases/sections/RecordingDetailsDialog.tsx